import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Form, Formik } from 'formik'
import styled from 'styled-components'
import { Button, H, P, Wrapper } from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'

import { SelectInput } from 'components/form'
import { formatCurrency } from 'lib/formatting/currency'
import { FuneralPlanPaymentDetails } from 'lib/models/funeral-plan'
import { Invoice } from 'lib/models/invoice'
import { cancelFuneralPlan, fetchFuneralPlan, showModal } from 'state/actions'
import { FUNERAL_PLAN_CANCELLATION_REASON_OPTIONS } from 'utils/enums'
import {
  stripeUrlForCustomerPage,
  stripeUrlForTransactionPage,
} from 'utils/helpers'

import CancelPlanModal from './cancel-plan-modal'

const StyledWrapper = styled(Wrapper)`
  border: 2px solid ${COLOR.STATE.WARNING};
  border-radius: 4px;
`

const StyledButtonsWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: baseline;
  gap: ${GTR.XS};
`

const StyledUnderlinedButton = styled(Button.Underline)`
  margin: 0;
  padding: 0;
`

export const CancelFuneralPlan = ({
  invoices,
  funeralPlanPaymentDetails,
  refreshDetails,
}: {
  invoices: Invoice[]
  funeralPlanPaymentDetails: FuneralPlanPaymentDetails
  refreshDetails: () => void
}): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const history = useHistory()
  const dispatch = useDispatch()
  const [cancellationReason, setCancellationReason] = useState<
    string | undefined
  >(undefined)

  const {
    amountToRefundInPence,
    packageCancellationFee,
    stripeCustomerId,
    totalPaidInPence,
    type,
  } = funeralPlanPaymentDetails.attributes
  const isInstalmentsCustomer = type === 'Instalments'
  const cancellationFeeInPence =
    totalPaidInPence > amountToRefundInPence
      ? packageCancellationFee
      : undefined
  const needsRefund = amountToRefundInPence > 0
  const stripeInvoices = invoices.filter(
    (invoice) => invoice.attributes.paymentProvider === 'stripe'
  )
  const handleGoToCustomerStripe = async () => {
    if (stripeCustomerId) {
      window.open(stripeUrlForCustomerPage(stripeCustomerId))
    } else {
      const firstInvoiceReference = stripeInvoices[0]?.attributes.reference
      if (!firstInvoiceReference) return
      window.open(stripeUrlForTransactionPage(firstInvoiceReference))
    }
  }

  const handleCancelPlan = async () => {
    try {
      if (!cancellationReason) {
        throw new Error('A reason for cancellation is required.')
      }
      await dispatch(cancelFuneralPlan(funeralPlanId, cancellationReason))
      await dispatch(fetchFuneralPlan(funeralPlanId))
      window.scrollTo(0, 0)
      history.push(`/funeral-plans/${funeralPlanId}`)
    } catch {
      // let the Backstage error handler show the error
    }
  }

  const handleCancelPlanModal = (): void => {
    if (needsRefund) return

    dispatch(
      showModal({
        component: CancelPlanModal,
        headingText: 'Confirm cancellation',
        config: {
          handleCancelPlan,
        },
      })
    )
  }

  return (
    <StyledWrapper padding={GTR.M}>
      <H tag="h2" size="M">
        Cancel plan
      </H>
      <P>To cancel this funeral plan, you must complete the following steps:</P>
      <Wrapper margin={[GTR.M, 0]}>
        <H tag="h3" size="S">
          1. Confirm with customer
        </H>
        <P>Confirm why the customer wants to cancel.</P>
        <Formik initialValues={{}} onSubmit={() => undefined}>
          <Form>
            <SelectInput
              label="Reason for cancellation"
              name="cancellationReason"
              options={FUNERAL_PLAN_CANCELLATION_REASON_OPTIONS}
              handleSave={({ value }) => {
                setCancellationReason(value as string)
              }}
            />
          </Form>
        </Formik>
      </Wrapper>
      <Wrapper margin={[GTR.M, 0]}>
        <H tag="h3" size="S">
          2. Issue the refund
        </H>
        {needsRefund ? (
          <>
            <P>
              Explain they’ll receive a refund of{' '}
              {formatCurrency({
                showPence: true,
                value: amountToRefundInPence,
                valueInPence: true,
              })}
              , which is the total amount they’ve paid
              {cancellationFeeInPence
                ? ` minus the ${formatCurrency({
                    showPence: true,
                    value: cancellationFeeInPence,
                    valueInPence: true,
                  })} cancellation fee`
                : ''}
              . We’ll send them an email to confirm.
            </P>
            {isInstalmentsCustomer && stripeCustomerId && (
              <P>Make sure the subscription is cancelled on Stripe as well.</P>
            )}
            <P>
              Amount to refund:{' '}
              {formatCurrency({
                showPence: true,
                value: amountToRefundInPence,
                valueInPence: true,
              })}
            </P>

            <StyledButtonsWrapper margin={[GTR.M, 0]}>
              {(stripeCustomerId || stripeInvoices.length > 0) && (
                <Button.Danger
                  type="button"
                  onClick={() => handleGoToCustomerStripe()}
                >
                  Issue refund on Stripe
                </Button.Danger>
              )}
            </StyledButtonsWrapper>
          </>
        ) : (
          <P>The customer has been refunded.</P>
        )}
      </Wrapper>
      <Wrapper margin={[GTR.M, 0]}>
        <H size="S" tag="h3">
          3. Cancel the plan
        </H>
        {needsRefund && (
          <P>
            Once you have processed the refund,{' '}
            <StyledUnderlinedButton onClick={refreshDetails}>
              click here to refresh the payment status
            </StyledUnderlinedButton>
            .
          </P>
        )}

        <StyledButtonsWrapper margin={[GTR.M, 0]}>
          <Button.Danger
            type="button"
            onClick={handleCancelPlanModal}
            disabled={needsRefund || !cancellationReason}
          >
            Cancel the plan
          </Button.Danger>
        </StyledButtonsWrapper>
      </Wrapper>
    </StyledWrapper>
  )
}
